import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import BatchSelector from '../../../Components/BatchSelector';

const RegistrationsList = () => {
  const { authUser } = useAuth();
  const role = authUser?.activeSession?.roleCode?.toLowerCase() || 'mentor';
  const currentlyBatchId = authUser?.activeSession?.batchId || 1;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(
    Number(searchParams.get('pageSize')) || 25,
  );
  const [batchId, setBatchId] = useState(
    Number(searchParams.get('batch')) || currentlyBatchId,
  );
  const [divisionInstitutionsId, setDivisionInstitutionsId] = useState(
    searchParams.get('divisionInstitutionsId') || '',
  );
  const [statusAcceptanceProgramId, setStatusAcceptanceProgramId] = useState(
    searchParams.get('statusAcceptanceProgramId') || '1,2,3,4,5,6',
  );
  const [institutionId, setInstitutionId] = useState('');

  const [loading, setLoading] = useState(true);
  const [loadingDivision, setLoadingDivision] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [users, setUsers] = useState({ students: [] });

  const [institutions, setInstitutions] = useState([]);
  const [provInstitutions, setProvInstitutions] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  // Fetch registrations
  const fetchRegistrations = useCallback(async () => {
    setLoading(true);

    try {
      const data = await api.getRegistrations({
        page,
        pageSize,
        batchId,
        divisionInstitutionsId,
        statusAcceptanceProgramId,
      });
      setUsers(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [
    page,
    pageSize,
    batchId,
    divisionInstitutionsId,
    statusAcceptanceProgramId,
  ]);

  // Fetch institutions and provinces
  const fetchInstitutionsData = useCallback(async () => {
    try {
      const institutionsData = await api.getInstitutionsOptions({ batchId });
      setInstitutions(institutionsData);
      const provData = await api.getInstitutionProvinces({ batchId });
      setProvInstitutions(provData);

      // Fetch status options
      const sOptions = await api.getRegistrationsStatusAcceptanceOptions({
        batchId,
      });
      setStatusOptions(sOptions);

      const urlStatus = new URLSearchParams(window.location.search).get(
        'statusAcceptanceProgramId',
      );
      const availableIds = sOptions.map((s) => s.id);

      if (urlStatus) {
        const currentIds = urlStatus.split(',').map(Number);
        const hasOverlap = currentIds.some((id) => availableIds.includes(id));
        if (hasOverlap) {
          setStatusAcceptanceProgramId(urlStatus);
          return;
        }
      }

      const allIds = availableIds.join(',');
      setStatusAcceptanceProgramId(allIds);
    } catch (err) {
      console.error(err);
    }
  }, [batchId]);

  useEffect(() => {
    fetchRegistrations();
    fetchInstitutionsData();

    // Update URL search params
    setSearchParams({
      page,
      pageSize,
      batch: batchId,
      divisionInstitutionsId,
      statusAcceptanceProgramId,
    });
  }, [
    page,
    pageSize,
    batchId,
    divisionInstitutionsId,
    statusAcceptanceProgramId,
  ]);

  // Handle division fetch when selecting institution
  const handleDivisiByInstitutionId = async (institutionId) => {
    setInstitutionId(institutionId);
    try {
      setLoadingDivision(true);
      const data = await api.getInstitutionsDivisionOptions({
        institutionId,
        batchId,
      });

      setDivisions(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDivision(false);
    }
  };

  // UX helper: reset page when batch/pageSize changes

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const handleDivisionChange = (e) => setDivisionInstitutionsId(e.target.value);
  const handleStatusChange = (e) =>
    setStatusAcceptanceProgramId(e.target.value);

  const onHandleStylePriority = (value) => {
    switch (value) {
      case 1:
        return 'success';
      case 2:
        return 'primary';
      case 3:
        return 'secondary';
      case 4:
        return 'warning';
      default:
        return 'danger';
    }
  };

  return (
    <div className='card card-body border-0 shadow-sm rounded-4'>
      <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4'>
        <div className='d-flex align-items-center gap-3'>
          <div
            className='bg-primary bg-opacity-10 d-flex align-items-center justify-content-center rounded-3'
            style={{ width: '40px', height: '40px' }}
          >
            <i className='bi bi-list-check text-primary fs-5'></i>
          </div>
          <div>
            <h1 className='h5 fw-bold mb-0 text-dark'>Pendaftaran</h1>
            <p
              className='text-muted mb-0 d-none d-lg-block'
              style={{ fontSize: '0.75rem' }}
            >
              Kelola data pendaftaran mahasiswa dan status penerimaan.
            </p>
          </div>
        </div>

        <div className='d-flex flex-wrap gap-2 align-items-center ms-auto'>
          {/* Page Size Selector */}
          <div className='d-flex align-items-center'>
            <label className='me-2 small text-muted'>Tampilkan:</label>
            <select
              value={pageSize}
              onChange={handlePageSizeChange}
              className='form-select form-select-sm shadow-sm rounded-pill px-3'
              style={{ minWidth: '80px', fontSize: '0.8rem' }}
            >
              {[25, 50, 100].map((size) => (
                <option
                  key={size}
                  value={size}
                >
                  {size}
                </option>
              ))}
            </select>
          </div>

          {/* Batch Filter Selector */}
          <div className='d-flex align-items-center ms-1'>
            <label className='me-2 fw-medium text-muted small mb-0 d-none d-xl-inline'>
              Batch:
            </label>
            <div style={{ minWidth: '150px' }}>
              <BatchSelector
                value={batchId}
                onChange={(id) => {
                  setBatchId(Number(id));
                  setPage(1);
                  setInstitutionId('');
                  setDivisionInstitutionsId('');
                  setDivisions([]);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className='bg-light bg-opacity-50 p-3 mb-4 rounded-4 border border-light-subtle'>
        <div
          className={`row row-cols-1 row-cols-md-${
            role === 'admin' ? '4' : '3'
          } g-3`}
        >
          {/* Provinsi (khusus Admin) */}
          {role === 'admin' && (
            <div className='col'>
              <div className='form-floating'>
                <select
                  value={divisionInstitutionsId}
                  onChange={handleDivisionChange}
                  className='form-select border-0 shadow-sm'
                >
                  <option value=''>Pilih</option>
                  {provInstitutions.map((prov) => (
                    <option
                      key={prov.divisionInstitutionsId}
                      value={prov.divisionInstitutionsId}
                    >
                      {prov.provinceName}
                    </option>
                  ))}
                </select>
                <label className='small'>Provinsi</label>
              </div>
            </div>
          )}

          {/* Lembaga */}
          <div className='col'>
            <div className='form-floating'>
              <select
                value={institutionId}
                onChange={(e) => handleDivisiByInstitutionId(e.target.value)}
                className='form-select border-0 shadow-sm'
              >
                <option value=''>Pilih Lembaga</option>
                {institutions.map((inst) => (
                  <option
                    key={inst.id}
                    value={inst.id}
                  >
                    {inst.label}
                  </option>
                ))}
              </select>
              <label className='small'>Lembaga</label>
            </div>
          </div>

          {/* Divisi */}
          <div className='col'>
            <div className='form-floating'>
              <select
                value={divisionInstitutionsId}
                disabled={!divisions.length || loadingDivision}
                onChange={handleDivisionChange}
                className='form-select border-0 shadow-sm'
              >
                <option value=''>
                  {loadingDivision ? 'Loading...' : 'Pilih Divisi'}
                </option>
                {divisions.map((div, key) => (
                  <option
                    key={key}
                    value={div.divisionInstitutionId}
                  >
                    {div.label} | Batch {div.batch}
                  </option>
                ))}
              </select>
              <label className='small'>Divisi</label>
            </div>
          </div>

          {/* Status Penerimaan */}
          <div className='col'>
            <div className='form-floating'>
              <select
                value={statusAcceptanceProgramId}
                onChange={handleStatusChange}
                className='form-select border-0 shadow-sm'
              >
                <option value={statusOptions.map((s) => s.id).join(',')}>
                  Semua
                </option>
                {statusOptions.map((s) => (
                  <option
                    key={s.id}
                    value={s.id}
                  >
                    {s.label}
                  </option>
                ))}
              </select>
              <label className='small'>Status Penerimaan</label>
            </div>
          </div>
        </div>
      </div>

      {refreshing && (
        <div className='text-center my-3'>
          <div className='spinner-border spinner-border-sm text-primary' />
          <span className='ms-2 small text-muted'>Memuat data baru...</span>
        </div>
      )}

      {/* Table */}
      <div className='table-responsive'>
        <table className='table table-hover align-middle mb-0'>
          <thead className='bg-light text-muted small text-uppercase fw-bold'>
            <tr>
              <th
                className='ps-4 border-0'
                style={{ width: '50px' }}
              >
                #
              </th>
              <th className='border-0'>Mahasiswa</th>
              <th className='border-0'>Program Studi</th>
              <th className='border-0'>Universitas</th>
              <th className='border-0'>IPK</th>
              <th className='border-0'>Provinsi Lembaga</th>
              <th className='border-0'>Divisi</th>
              <th className='border-0'>Domisili</th>
              <th className='border-0'>Berkas</th>
              <th className='border-0'>Prioritas</th>
              <th className='border-0 pe-4 text-end'>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: pageSize || 10 }).map((_, idx) => (
                <tr
                  key={idx}
                  className='placeholder-glow'
                >
                  <td className='ps-4 py-3'>
                    <span className='placeholder col-12 rounded'></span>
                  </td>
                  {Array.from({ length: 9 }).map((_, i) => (
                    <td
                      key={i}
                      className='py-3'
                    >
                      <span className='placeholder col-12 rounded'></span>
                    </td>
                  ))}
                  <td className='pe-4 py-3 text-end'>
                    <span className='placeholder col-8 rounded'></span>
                  </td>
                </tr>
              ))
            ) : users?.students?.length > 0 ? (
              users.students.map((student, idx) => (
                <tr
                  key={idx}
                  className='transition-all'
                >
                  <td className='ps-4 text-muted small'>
                    {(page - 1) * pageSize + (idx + 1)}
                  </td>
                  <td>
                    <div className='fw-bold text-dark'>
                      {student.firstName} {student.lastName}
                    </div>
                  </td>
                  <td>
                    <div className='small text-dark'>
                      {student.universities.universitiesMajor}
                    </div>
                  </td>
                  <td>
                    <div className='small text-muted'>
                      {student.universities.universitiesName}
                    </div>
                  </td>
                  <td className='text-center fw-medium'>
                    {student.universities.gradePointAverage}
                  </td>
                  <td>
                    <div className='small text-dark'>
                      {student.institutions.institutionsLocation.split(',')[0]}
                    </div>
                    <div className='extra-small text-muted'>
                      {student.institutions.institutionsLocation.split(',')[1]}
                    </div>
                  </td>
                  <td>
                    <span className='badge bg-light text-dark border px-2 py-1 extra-small fw-medium'>
                      {student.institutions.institutionsDivisionName}
                    </span>
                  </td>
                  <td>
                    {student.domicilies.map((value, key) => (
                      <div
                        key={key}
                        className={key > 0 ? 'mt-1 pt-1 border-top' : ''}
                      >
                        <div className='extra-small text-dark fw-medium'>
                          {key === 0 ? 'KTP: ' : 'Sekarang: '}
                          {value.provinceName}
                        </div>
                        <div
                          className='text-muted'
                          style={{ fontSize: '0.65rem' }}
                        >
                          {value.regenciesName}
                        </div>
                      </div>
                    ))}
                  </td>
                  <td>
                    <div className='d-flex flex-column gap-1'>
                      <a
                        href={student.curriculumVitae}
                        target='_blank'
                        rel='noreferrer'
                        className='text-decoration-none extra-small fw-bold text-primary d-flex align-items-center gap-1'
                      >
                        <i className='bi bi-file-earmark-person'></i> CV
                      </a>
                      <a
                        href={student.academicTranscript}
                        target='_blank'
                        rel='noreferrer'
                        className='text-decoration-none extra-small fw-bold text-success d-flex align-items-center gap-1'
                      >
                        <i className='bi bi-file-earmark-text'></i> Transkrip
                      </a>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`badge rounded-pill bg-${onHandleStylePriority(
                        student.priority.priorityValue,
                      )} bg-opacity-10 text-${onHandleStylePriority(
                        student.priority.priorityValue,
                      )} border border-${onHandleStylePriority(
                        student.priority.priorityValue,
                      )} border-opacity-25 extra-small`}
                    >
                      {student.priority.priorityMessage}
                    </span>
                  </td>
                  <td className='pe-4 text-end'>
                    <Link
                      to={`/${role}/registrations/${student.CLPUsersId}`}
                      className='btn btn-outline-primary btn-sm rounded-pill extra-small fw-bold px-3'
                    >
                      Detail
                    </Link>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan='11'
                  className='text-center py-5'
                >
                  <div className='text-muted mb-3'>
                    <i className='bi bi-inbox fs-1 opacity-25'></i>
                  </div>
                  <h6 className='fw-bold text-dark'>
                    Tidak ada data ditemukan
                  </h6>
                  <p className='text-muted small mb-0'>
                    Gunakan filter untuk menemukan data lainnya.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Improved Pagination */}
      <div className='mt-4 pt-4 border-top'>
        <nav aria-label='Registrations navigation'>
          <ul className='pagination pagination-sm justify-content-center mb-0 gap-2'>
            <li className={`page-item ${!users.previous ? 'disabled' : ''}`}>
              <button
                onClick={() => users.previous && setPage(users.previous)}
                className='page-link border-0 rounded-pill bg-light text-dark px-3 fw-bold'
                disabled={!users.previous}
              >
                <i className='bi bi-chevron-left'></i>
              </button>
            </li>
            <li className='page-item active shadow-sm rounded-pill'>
              <span className='page-link border-0 rounded-pill bg-primary text-white px-3 fw-bold'>
                {users.current + 1 || page}
              </span>
            </li>
            <li className={`page-item ${!users.next ? 'disabled' : ''}`}>
              <button
                onClick={() => users.next && setPage(users.next + 1)}
                className='page-link border-0 rounded-pill bg-light text-dark px-3 fw-bold'
                disabled={!users.next}
              >
                <i className='bi bi-chevron-right'></i>
              </button>
            </li>
          </ul>
        </nav>
      </div>

      <style>{`
        .extra-small { font-size: 0.7rem; }
        .transition-all { transition: all 0.2s ease; }
        .table-hover tbody tr:hover {
          background-color: rgba(var(--bs-primary-rgb), 0.03) !important;
          transform: scale(0.998);
        }
      `}</style>
    </div>
  );
};

export default RegistrationsList;
